import React, { useState, useEffect, useRef } from 'react';
import Icon from '@mdi/react';
import { mdiClose, mdiAlertCircleOutline, mdiAccountSearchOutline, mdiCalendarClock, mdiAirplaneTakeoff } from '@mdi/js';
import { toast } from 'react-toastify';
import { bookingApi } from '../api/booking';
import webApiClient from '../api/client';
import DateInputWithPicker from './DateInputWithPicker';
import TimeSelectDropdown from './TimeSelectDropdown';
import { formatCurrencyFromMinor } from '../utils/currency';
import useEscapeKey from '../hooks/useEscapeKey';
import './ManualBookingModal.css';

const timeToMinutes = (timeString) => {
  const [hour, minute] = timeString.split(':').map(Number);
  return hour * 60 + minute;
};

const addMinutesToTime = (timeString, minutes) => {
  const total = (timeToMinutes(timeString) + minutes + 24 * 60) % (24 * 60);
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

const formatTime12hr = (timeString) => {
  if (!timeString) return '';
  const [h, m] = timeString.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${hour12}:${String(m).padStart(2, '0')} ${period}`;
};

const findTravelPeriodForDate = (isoDate, periods) => {
  if (!isoDate || !periods || !periods.length) return null;
  return periods.find(period => {
    const start = period.startDate.substring(0, 10);
    const end = period.endDate.substring(0, 10);
    return isoDate >= start && isoDate <= end;
  }) || null;
};

/**
 * ManualBookingModal - Create bookings for non-app users or users with app issues
 * Allows instructors to manually enter booking details with optional user search
 */
const ManualBookingModal = ({
  isOpen,
  selectedDate,
  initialTime = null,
  initialEndTime = null,
  instructorProfile,
  instructorOrgId, // Required: instructor's organization ID
  existingBookings = [],
  travelPeriods = [],
  onClose,
  onSave
}) => {
  // Close on Escape (stack-aware shared hook) — mirrors the X/overlay `dismiss`
  useEscapeKey(isOpen, () => dismiss());

  const [formData, setFormData] = useState({
    studentName: '',
    studentEmail: '',
    userId: null,
    date: selectedDate ? selectedDate.toISODate() : '',
    startTime: '09:00',
    endTime: '10:00',
    locationId: '',
    locationDescription: ''
  });

  // When the modal is opened from a slot click, start/end are locked to the
  // slot's bounds (the booking must match the availability slot exactly).
  // When opened from the toolbar (no slot context), the instructor can freely
  // enter both times.
  const isSlotLocked = initialTime != null && initialEndTime != null;

  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  // Wraps the name input + its search-results dropdown; used to close the
  // dropdown when the instructor clicks anywhere outside it.
  const nameSearchRef = useRef(null);
  const [timeConflict, setTimeConflict] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [matchedTravelPeriod, setMatchedTravelPeriod] = useState(null);
  const [studentCreditBalance, setStudentCreditBalance] = useState(null);
  const [creditInput, setCreditInput] = useState('');
  // After the booking is created we know its real cost — switch to a credit step
  // that shows the total + balance so the instructor applies credit against the
  // exact amount (rather than guessing before creation).
  const [createdBooking, setCreatedBooking] = useState(null);
  const [isApplyingCredit, setIsApplyingCredit] = useState(false);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen && selectedDate) {
      const isoDate = selectedDate.toISODate();
      const startTime = initialTime ?? '09:00';
      const endTime = initialEndTime ?? addMinutesToTime(startTime, 60);
      setFormData({
        studentName: '',
        studentEmail: '',
        userId: null,
        date: isoDate,
        startTime,
        endTime,
        locationId: '',
        locationDescription: ''
      });
      setSearchResults([]);
      setShowSearchResults(false);
      setTimeConflict(null);
      setMatchedTravelPeriod(findTravelPeriodForDate(isoDate, travelPeriods));
    }
  }, [isOpen, selectedDate]);

  // Close the name-search dropdown when clicking anywhere outside it.
  useEffect(() => {
    if (!showSearchResults) return;
    const handleClickOutside = (e) => {
      if (nameSearchRef.current && !nameSearchRef.current.contains(e.target)) {
        setShowSearchResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSearchResults]);

  // Re-evaluate travel period when date changes or travelPeriods updates
  useEffect(() => {
    setMatchedTravelPeriod(findTravelPeriodForDate(formData.date, travelPeriods));
  }, [travelPeriods, formData.date]);

  // Auto-pin the appropriate default location based on date context:
  //   - Travel-period date → travel period's defaultLocationId (or TBA if unset)
  //   - Home date → the instructor's isDefault: true home location (or TBA if none)
  // The dropdown's filter already orders the pinned/default option first
  // visually, but the form state has to be set explicitly for the <select>
  // to render that option as selected.
  useEffect(() => {
    if (matchedTravelPeriod) {
      setFormData(prev => ({
        ...prev,
        locationId: matchedTravelPeriod.defaultLocationId || ''
      }));
    } else {
      const homeDefault = (instructorProfile?.instructorProfile?.preferredLocations || []).find(
        l => l.isActive !== false && l.isTravel !== true && l.isDefault === true
      );
      setFormData(prev => ({
        ...prev,
        locationId: homeDefault?.id || ''
      }));
    }
  }, [matchedTravelPeriod?.id, matchedTravelPeriod?.defaultLocationId, instructorProfile]);

  // Toolbar-entry path only: auto-fill end time to start + 1 hour when the
  // user changes start. Slot-click path is locked so this effect is a no-op
  // (initial state was already set from the slot's bounds).
  useEffect(() => {
    if (isSlotLocked) return;
    if (formData.startTime) {
      const endTime = addMinutesToTime(formData.startTime, 60);
      setFormData(prev => (prev.endTime === endTime ? prev : { ...prev, endTime }));
    }
  }, [formData.startTime, isSlotLocked]);

  // Check for time overlap
  useEffect(() => {
    if (formData.date && formData.startTime && formData.endTime) {
      const conflict = existingBookings.find(booking => {
        const bookingDate = booking.bookingDate.split('T')[0];
        if (bookingDate !== formData.date) return false;

        const formStart = timeToMinutes(formData.startTime);
        const formEnd = timeToMinutes(formData.endTime);
        const bookingStart = timeToMinutes(booking.timeSlot.startTime);
        const bookingEnd = timeToMinutes(booking.timeSlot.endTime);

        return (formStart < bookingEnd && formEnd > bookingStart);
      });

      setTimeConflict(conflict);
    }
  }, [formData.date, formData.startTime, formData.endTime, existingBookings]);

  // Fetch credit balance when a registered student is selected
  useEffect(() => {
    const userId = formData.userId;
    if (userId && userId !== '000000000000000000000000') {
      bookingApi.getCreditBalance(userId)
        .then(data => setStudentCreditBalance({ balance: data.creditBalance, currency: data.currency }))
        .catch(() => setStudentCreditBalance(null));
    } else {
      setStudentCreditBalance(null);
      setCreditInput('');
    }
  }, [formData.userId]);

  // Reset the credit step whenever the modal closes so a reopen starts on the form
  useEffect(() => {
    if (!isOpen) {
      setCreatedBooking(null);
      setIsApplyingCredit(false);
      setCreditInput('');
    }
  }, [isOpen]);

  // (timeToMinutes / addMinutesToTime are module-level helpers below.)

  // Handle name input - search for existing users
  const handleNameChange = async (e) => {
    const name = e.target.value;
    setFormData(prev => ({ ...prev, studentName: name, userId: null }));

    if (name.length >= 3) {
      setIsSearching(true);
      setShowSearchResults(true);

      try {
        // Search for existing users in database
        const response = await webApiClient.get(`/api/v2/account/search?name=${encodeURIComponent(name)}`);

        if (response.data.success) {
          setSearchResults(response.data.users || []);
        } else {
          setSearchResults([]);
        }
      } catch (error) {
        console.error('Error searching users:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    } else {
      setSearchResults([]);
      setShowSearchResults(false);
      setIsSearching(false);
    }
  };

  const handleSelectUser = (user) => {
    setFormData(prev => ({
      ...prev,
      studentName: user.displayName || user.name,
      studentEmail: user.email || '',
      userId: user.id
    }));
    setShowSearchResults(false);
  };

  const handleLocationChange = (e) => {
    const locationId = e.target.value;
    const location = instructorProfile?.instructorProfile?.preferredLocations?.find(
      loc => loc.id === locationId
    );

    setFormData(prev => ({
      ...prev,
      locationId,
      locationDescription: location?.description || ''
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Guests aren't in the app — confirmation and receipt emails are their only
    // notification channel, so a valid email is mandatory for a guest booking.
    const submitIsGuest = !formData.userId || formData.userId === '000000000000000000000000';
    if (submitIsGuest) {
      const email = formData.studentEmail.trim();
      if (!email) {
        toast.error('Email is required for a guest booking so we can send the confirmation and receipt.');
        return;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        toast.error('Enter a valid email address for the guest.');
        return;
      }
    }

    const nameParts = formData.studentName.trim().split(/\s+/);
    const firstName = nameParts[0] || formData.studentName;
    const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : firstName;

    const bookingData = {
      instructorId: instructorOrgId,
      bookingDate: formData.date,
      timeSlot: {
        startTime: formData.startTime,
        endTime: formData.endTime,
      },
      studentInfo: {
        firstName,
        lastName,
        email: formData.studentEmail || '',
        phone: '',
        // studentInfo.message must remain empty for manual bookings.
        // The student is auto-notified via a chat message inserted by booking.service.js
        // (messagesService.sendMessage) — that is the canonical surface, and it appears
        // in the booking-messages thread on both mobile and web. Do NOT write a
        // disclaimer or system string here: doing so renders as an orphaned
        // "Student Message" section on mobile booking details. (Backend also
        // defensively strips this field for isManualEntry=true, but keep it
        // clean here too.)
        message: ''
      },
      location: (() => {
        if (!formData.locationId) {
          return {
            type: 'instructor_studio',
            locationId: null,
            snapshot: {
              description: 'Location TBA',
              locationDescription: undefined,
              address: '',
              latitude: null,
              longitude: null,
              geo: null
            }
          };
        }

        const selectedLocation = instructorProfile?.instructorProfile?.preferredLocations?.find(
          loc => loc.id === formData.locationId
        );

        return {
          type: 'instructor_studio',
          locationId: selectedLocation?.id || null,
          snapshot: {
            description: selectedLocation?.description || 'Location TBA',
            locationDescription: selectedLocation?.locationDescription || undefined,
            address: selectedLocation?.address || '',
            latitude: selectedLocation?.latitude || null,
            longitude: selectedLocation?.longitude || null,
            geo: selectedLocation?.geo || null
          }
        };
      })(),
      isManualEntry: true, // Always true - created manually by instructor (not through student app)
      userId: formData.userId || null
    };

    const savedBooking = await onSave(bookingData);
    if (!savedBooking?.id) return; // save failed — parent already showed the error toast

    // Always show the confirmation step (pricing summary + what-happens-next),
    // with a credit-apply option layered in when the student has credit.
    setCreditInput('');
    setCreatedBooking(savedBooking);
  };

  const handleApplyCredit = async () => {
    const creditValue = parseFloat(creditInput) || 0;
    if (creditValue <= 0) {
      // Nothing to apply — send the payment email for the full balance, then close.
      await maybeSendPaymentEmail();
      onClose();
      return;
    }
    setIsApplyingCredit(true);
    try {
      const result = await bookingApi.applyCredit({
        studentId: formData.userId,
        bookingId: createdBooking.id,
        creditAmountToApply: Math.round(creditValue * 100),
      });
      const currency = createdBooking.pricing?.currency || studentCreditBalance?.currency || 'AUD';
      // appliedAmount is minor units (cents) — use the zero-decimal-aware helper
      const applied = formatCurrencyFromMinor(result?.appliedAmount ?? Math.round(creditValue * 100), currency);
      toast.success(
        result?.fullyPaid
          ? `Booking paid in full with ${applied} credit`
          : `Credit of ${applied} applied`
      );
      // If a balance remains (partial), email the student the NET amount due.
      if (!result?.fullyPaid) await maybeSendPaymentEmail();
      onClose();
    } catch (err) {
      toast.error('Could not apply credit. Please try again.');
      setIsApplyingCredit(false);
    }
  };

  // Send the after-lesson payment email for the remaining balance (manual bookings
  // defer the email so credit can be applied first; fully credit-paid → no email).
  const maybeSendPaymentEmail = async () => {
    const cb = createdBooking;
    if (!cb?.id) return;
    const afterLesson = cb.pricing?.paymentTiming === 'after-lesson';
    const hasEmail = !!(cb.studentInfo?.email || formData.studentEmail);
    if (!afterLesson || !hasEmail) return;
    try {
      await bookingApi.sendPaymentEmail(cb.id);
    } catch (err) {
      // Non-fatal for the booking itself, but the instructor must know the
      // student was NOT emailed — they can resend from booking details.
      console.error('Failed to send booking payment email:', err);
      toast.error('Payment email could not be sent. You can resend it from the booking details.');
    }
  };

  // "Done" without applying credit → a balance remains, so email the student.
  const handleDone = async () => {
    setIsApplyingCredit(true);
    await maybeSendPaymentEmail();
    onClose();
  };

  // X / overlay dismiss: on the confirmation step this is the same as Done (the
  // booking already exists, so the student still needs the payment email); on the
  // form step it's a plain cancel (nothing created yet).
  const dismiss = () => {
    if (isApplyingCredit) return;
    if (createdBooking) { handleDone(); } else { onClose(); }
  };

  if (!isOpen) return null;

  // Confirmation-step derived values (null-safe; createdBooking is set after save)
  const cbPricing = createdBooking?.pricing || {};
  // Server-computed display pricing (effectivePricing) — single source of truth.
  // Principal = lesson fee + venue hire (the instructor's money; what credit can
  // cover). The service fee is added only at the student's online checkout. Never
  // compute booking prices client-side.
  const cbEp = createdBooking?.effectivePricing || {};
  const cbCurrency = cbEp.currency || cbPricing.currency || 'AUD';
  const cbPrincipal = cbEp.principal || 0;
  const cbIsGuest = !formData.userId || formData.userId === '000000000000000000000000';
  const cbEmail = createdBooking?.studentInfo?.email || formData.studentEmail;
  const cbPaymentEmailed = createdBooking?.status === 'confirmed'
    && cbPricing.paymentTiming === 'after-lesson'
    && !!cbEmail;
  const cbHasCredit = !!(studentCreditBalance && studentCreditBalance.balance > 0 && !cbIsGuest);
  const creditValue = parseFloat(creditInput) || 0;
  // creditInput/creditValue are DOLLARS; cbPrincipal + balance are integer cents.
  // The cap is dollar-denominated to match the input, so bring both to dollars.
  const creditCap = Math.min(cbPrincipal / 100, (studentCreditBalance?.balance || 0) / 100);
  // Credit is a single currency-agnostic balance (held in the instructor's home
  // currency). A travel-period booking can be in a DIFFERENT currency, and we do
  // NOT do FX conversion — applying credit 1:1 across currencies would silently
  // mis-value it. So when the booking currency differs from the credit currency,
  // block the apply-credit path and explain why. (Future: real FX conversion — see
  // docs/business/ROADMAP.md.)
  const creditCurrency = (studentCreditBalance?.currency || 'AUD').toUpperCase();
  const creditCurrencyMismatch =
    cbHasCredit && creditCurrency !== (cbCurrency || 'AUD').toUpperCase();
  const cbCanApplyCredit = cbHasCredit && !creditCurrencyMismatch;

  const locations = instructorProfile?.instructorProfile?.preferredLocations || [];

  // Travel-context-aware FILTER for the location dropdown.
  // Inside a travel period: show only travel locations, with the travel context's
  // defaultLocationId pinned first (even if it isn't flagged isTravel — the
  // instructor explicitly chose it for this period).
  // Outside a travel period: show only home locations.
  const sortedLocationsForDropdown = (() => {
    const travelDefaultId = matchedTravelPeriod?.defaultLocationId || null;
    const active = locations.filter(l => l.isActive !== false);

    if (matchedTravelPeriod) {
      const travel = active.filter(l => l.isTravel === true);
      const pinned = travelDefaultId ? active.find(l => l.id === travelDefaultId) : null;
      const restTravel = pinned ? travel.filter(l => l.id !== travelDefaultId) : travel;
      return [
        ...(pinned ? [pinned] : []),
        ...restTravel,
      ];
    }
    return active.filter(l => l.isTravel !== true);
  })();

  // Guest = no existing student selected from search. Guests are not in the app,
  // so the booking confirmation and payment receipt can only reach them by email —
  // make the email mandatory for guests (it stays auto-filled + locked for students).
  const isGuest = !formData.userId || formData.userId === '000000000000000000000000';
  const emailTrimmed = formData.studentEmail.trim();
  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrimmed);
  const guestEmailMissing = isGuest && !emailTrimmed;
  const guestEmailInvalid = isGuest && !!emailTrimmed && !isValidEmail;
  const canSave = !!formData.studentName.trim() && !guestEmailMissing && !guestEmailInvalid;

  return (
    <div className="manual-booking-overlay" onClick={dismiss}>
      <div className="manual-booking-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="manual-booking-header">
          <div className="manual-booking-header-content">
            <Icon path={mdiCalendarClock} size={1.2} color="var(--dl-color-purple-default)" />
            <h2>{createdBooking ? 'Booking Created' : (isSlotLocked ? 'Book This Slot' : 'Add Manual Booking')}</h2>
          </div>
          <button className="manual-booking-close" onClick={dismiss} disabled={isApplyingCredit}>
            <Icon path={mdiClose} size={1.2} />
          </button>
        </div>

        {/* Body — booking form (hidden once we move to the credit step) */}
        {!createdBooking && (
        <div className="manual-booking-body">
          <form onSubmit={handleSubmit}>
            {/* Student Name (with search) */}
            <div className="form-group" ref={nameSearchRef}>
              <label htmlFor="studentName">Student Name *</label>
              <p className="form-hint">Search for existing users or enter name manually</p>
              <input
                type="text"
                id="studentName"
                value={formData.studentName}
                onChange={handleNameChange}
                required
                placeholder="Start typing to search..."
                autoComplete="off"
              />

              {/* Search Results Dropdown */}
              {showSearchResults && (
                <div className="search-results">
                  {isSearching && (
                    <div className="search-loading">Searching...</div>
                  )}

                  {!isSearching && searchResults.length === 0 && (
                    <div className="search-no-results">
                      <Icon path={mdiAccountSearchOutline} size={1.2} />
                      <p>No existing users found</p>
                      <span>You can still enter the name manually</span>
                    </div>
                  )}

                  {!isSearching && searchResults.length > 0 && searchResults.map(user => (
                    <div
                      key={user.id}
                      className="search-result-item"
                      onClick={() => handleSelectUser(user)}
                    >
                      <div className="user-name">{user.displayName || user.name}</div>
                      {user.email && <div className="user-email">{user.email}</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Email — required for guests (drives confirmation + receipt emails),
                auto-filled and locked for an existing student. */}
            <div className="form-group">
              <label htmlFor="studentEmail">
                {isGuest ? 'Email *' : 'Email'}
              </label>
              {isGuest && (
                <p className="form-hint">Booking confirmation and payment receipt are emailed here</p>
              )}
              <input
                type="text"
                id="studentEmail"
                value={formData.studentEmail}
                onChange={(e) => setFormData(prev => ({ ...prev, studentEmail: e.target.value }))}
                onFocus={() => setShowSearchResults(false)}
                placeholder="student@example.com"
                disabled={!!formData.userId}
                required={isGuest}
              />
              {guestEmailInvalid && (
                <p className="form-error">Enter a valid email address.</p>
              )}
            </div>

            {/* Date */}
            <div className="form-group">
              <label htmlFor="date">Date *</label>
              <DateInputWithPicker
                id="date"
                value={formData.date}
                onChange={(isoDate) => {
                  setFormData(prev => ({ ...prev, date: isoDate }));
                  setShowSearchResults(false);
                  setMatchedTravelPeriod(findTravelPeriodForDate(isoDate, travelPeriods));
                }}
                required
              />
            </div>

            {matchedTravelPeriod && (
              <div
                className="manual-booking-modal-travel-banner"
                style={{
                  backgroundColor: `${matchedTravelPeriod.color}1A`,
                  borderLeftColor: matchedTravelPeriod.color
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <Icon
                    path={mdiAirplaneTakeoff}
                    size={0.9}
                    style={{ color: matchedTravelPeriod.color, flexShrink: 0 }}
                  />
                  <span className="manual-booking-modal-travel-banner-text">
                    Travel availability in {matchedTravelPeriod.destination}
                  </span>
                </span>
              </div>
            )}

            {/* Time Row */}
            {isSlotLocked ? (
              <div className="form-group manual-booking-modal-slot-time-locked">
                <label>Time</label>
                <p className="manual-booking-modal-slot-time-display">
                  {formatTime12hr(formData.startTime)} – {formatTime12hr(formData.endTime)}
                </p>
                <p className="form-hint">
                  If you need to change this slot, edit your availability, or edit the booking time after you've booked it.
                </p>
              </div>
            ) : (
              <div className="form-row">
                <div
                  className="form-group"
                  onMouseDown={() => setShowSearchResults(false)}
                >
                  <label htmlFor="startTime">Start Time *</label>
                  <TimeSelectDropdown
                    value={formData.startTime}
                    onChange={(v) => setFormData(prev => ({ ...prev, startTime: v }))}
                    ariaLabel="Start time"
                  />
                </div>

                <div
                  className="form-group"
                  onMouseDown={() => setShowSearchResults(false)}
                >
                  <label htmlFor="endTime">End Time *</label>
                  <TimeSelectDropdown
                    value={formData.endTime}
                    onChange={(v) => setFormData(prev => ({ ...prev, endTime: v }))}
                    ariaLabel="End time"
                  />
                </div>
              </div>
            )}

            {/* Time Conflict Warning */}
            {timeConflict && (
              <div className="conflict-warning">
                <Icon path={mdiAlertCircleOutline} size={0.9} />
                <span>
                  Time conflicts with existing booking for {timeConflict.studentName}
                  ({timeConflict.timeSlot.startTime} - {timeConflict.timeSlot.endTime})
                </span>
              </div>
            )}

            {/* Location */}
            <div className="form-group">
              <label htmlFor="location">Location</label>
              <select
                id="location"
                value={formData.locationId}
                onChange={handleLocationChange}
                onFocus={() => setShowSearchResults(false)}
              >
                <option value="">Location TBA</option>
                {sortedLocationsForDropdown.map(loc => {
                  const isTravelDefault = matchedTravelPeriod && loc.id === matchedTravelPeriod.defaultLocationId;
                  return (
                    <option key={loc.id} value={loc.id}>
                      {isTravelDefault ? '★ ' : ''}{loc.description}
                    </option>
                  );
                })}
              </select>
            </div>
          </form>
        </div>
        )}

        {/* Confirmation step — shown after the booking is created, using its real cost */}
        {createdBooking && (
          <div className="manual-booking-body">
            {/* Lesson + venue total (the instructor's money; what credit can cover).
                The service fee is added at the student's online checkout — shown to the
                student there, not here. */}
            <div className="manual-booking-summary">
              <div className="manual-booking-summary-row">
                <span>Lesson fee</span>
                <span>{formatCurrencyFromMinor(cbEp.lessonFee || 0, cbCurrency)}</span>
              </div>
              {(cbEp.venueFee || 0) > 0 && (
                <div className="manual-booking-summary-row">
                  <span>Venue hire</span>
                  <span>{formatCurrencyFromMinor(cbEp.venueFee, cbCurrency)}</span>
                </div>
              )}
              <div className="manual-booking-summary-row manual-booking-summary-total">
                <span>Total due</span>
                <span>
                  {formatCurrencyFromMinor(cbPrincipal, cbCurrency)}
                  {cbPricing.paymentTiming === 'after-lesson' ? ' · pay after lesson' : ''}
                </span>
              </div>
              <p className="manual-booking-summary-hint">Service fee added if the student pays online.</p>
            </div>

            {/* What happens next */}
            <div className="manual-booking-next">
              <p className="manual-booking-next-title">What happens next</p>
              <ul>
                {cbIsGuest ? (
                  <li>This is a guest booking, so they won't be notified in the app.</li>
                ) : (
                  <>
                    <li>{formData.studentName || 'The student'} will see this booking in their Dance Localé app.</li>
                    <li>They've been messaged about it (and notified if they have the app).</li>
                  </>
                )}
                {cbPaymentEmailed && (
                  <li>When you finish, we'll email {cbEmail} a payment request, or a paid-in-full confirmation if credit covers the booking.</li>
                )}
              </ul>
            </div>

            {/* Credit currency mismatch — credit can't be applied to a booking in a
                different currency (no FX conversion). Quiet note, no amount teased. */}
            {creditCurrencyMismatch && (
              <div className="manual-booking-credit-section">
                <p className="form-hint">
                  {(formData.studentName || 'This student')}'s account credit is held in {creditCurrency} and can't be applied to a {(cbCurrency || '').toUpperCase()} booking. The student can pay by card.
                </p>
              </div>
            )}

            {/* Credit application — only when the student has credit in the booking's currency */}
            {cbCanApplyCredit && (
              <div className="manual-booking-credit-section">
                <p className="manual-booking-credit-label">
                  {(formData.studentName || 'Student')} has{' '}
                  {formatCurrencyFromMinor(studentCreditBalance.balance, studentCreditBalance.currency)} in credit
                </p>
                <label htmlFor="credit-to-apply">Apply credit amount</label>
                <p className="form-hint">Covers the lesson + venue total. Cannot exceed {formatCurrencyFromMinor(cbPrincipal, cbCurrency)} or the available balance.</p>
                <input
                  id="credit-to-apply"
                  type="number"
                  min={0}
                  max={creditCap}
                  step="1"
                  placeholder="0.00"
                  value={creditInput}
                  onChange={e => {
                    const v = e.target.value;
                    if (v === '') { setCreditInput(''); return; }
                    const n = parseFloat(v);
                    // Clamp only when over the cap; otherwise keep the raw string so
                    // decimals (e.g. "5." while typing) aren't wiped.
                    setCreditInput(Number.isFinite(n) && n > creditCap ? String(creditCap) : v);
                  }}
                  className="manual-booking-input"
                />
                {creditValue > 0 && (
                  <p className="manual-booking-credit-remaining">
                    {creditValue >= cbPrincipal / 100 ? (
                      <strong>Paid in full by credit. No card payment needed.</strong>
                    ) : (
                      <>
                        Remaining lesson + venue:{' '}
                        <strong>{formatCurrencyFromMinor(Math.max(0, cbPrincipal - Math.round(creditValue * 100)), cbCurrency)}</strong>
                        {' '}(plus service fee if paid online)
                      </>
                    )}
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="manual-booking-footer">
          {createdBooking ? (
            cbCanApplyCredit ? (
              <>
                <button type="button" className="btn-cancel" onClick={handleDone} disabled={isApplyingCredit}>
                  Close
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={handleApplyCredit}
                  disabled={isApplyingCredit || creditValue <= 0}
                >
                  {isApplyingCredit ? 'Applying...' : 'Apply Credit'}
                </button>
              </>
            ) : (
              <button type="button" className="btn-primary" onClick={handleDone} disabled={isApplyingCredit}>
                Done
              </button>
            )
          ) : (
            <>
              <button type="button" className="btn-cancel" onClick={onClose}>
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
                onClick={handleSubmit}
                disabled={!canSave}
              >
                Save Booking
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManualBookingModal;
